import { makeVar } from "@apollo/client";
import type { DocumentCustomFieldsDraft } from "@/lib/documentCustomFields";
import * as Sentry from "@sentry/nextjs";
import {
	ApplicationProgressCustomFieldSectionValue,
	FixedPointNumber,
	OfferPrimaryDocumentType,
} from "../types";

export type ApplicationProgressAdviserFeeValue = {
	valueType: "PERCENTAGE" | "DOLLAR";
	value: string;
};

export type ApplicationProgressValue = {
	/** APP-29152: serialisable IDs/filenames only; scoped again to account, offer and entity. */
	documentCustomFields?: DocumentCustomFieldsDraft;
	investingEntityId: string | null;
	/**
	 * Whether the investor created a new investing entity during this
	 * application (vs. selecting an existing one). Gates the optional
	 * additional-documents step, which is only relevant for new entities.
	 */
	createdInvestingEntity: boolean | null;
	completedSteps: string[];
	activeStep: string | null;
	unitCountDecimal: Omit<FixedPointNumber, "__typename"> | null;
	investmentAmountHPM: { amount: string; currencyId: string } | null;
	investorPreference: string | null;
	reinvestmentPreference: boolean | null;
	hasDownloadedOfferDocument: boolean | null;
	/**
	 * Which per-type primary document the investor last downloaded. Lets the
	 * forced download reset when the declaration (and therefore the required
	 * document) changes on a WHOLESALE_RETAIL raise, without having to remember
	 * to clear a boolean. `null` on the legacy single-document path.
	 */
	downloadedOfferDocumentType: OfferPrimaryDocumentType | null;
	declaredInvestorType: "RETAIL" | "WHOLESALE" | null;
	/**
	 * Whether the investor certified they are a wholesale investor at the
	 * invest-page gate. Recorded on the application in place of the removed
	 * Confirmation-step acknowledgement checkbox.
	 */
	hasCertifiedWholesale: boolean | null;
	isAdvised: boolean | null;
	adviserEmail: string | null;
	hasAdviserFees: boolean | null;
	initialAdviserFee: ApplicationProgressAdviserFeeValue | null;
	ongoingAdviserFee: ApplicationProgressAdviserFeeValue | null;
	customFieldSections: Array<{
		id: string;
		values: Array<
			Omit<ApplicationProgressCustomFieldSectionValue, "__typename">
		>;
	}>;
};

const APPLICATION_PROGRESS_KEY_PREFIX = "ApplicationProgressV3:Offer:";
const SUPERSEDED_KEY_PREFIX = "ApplicationProgressV2:Offer:";

/**
 * The localStorage key for one offer's application progress.
 *
 * Authenticated progress is keyed by account as well as offer: the record
 * carries the investor-type declaration deciding which disclosure document the
 * investor is served, plus compliance-adjacent step markers, none of which may
 * reach another account on the same browser (APP-26441).
 *
 * The version is V3 because a V2 key is indistinguishable from this scheme's
 * signed-out key. Leaving it at V2 would hand every pre-deploy record — written
 * by a signed-in investor — to the next signed-out visitor.
 *
 * Module-private: every caller goes through `persistApplicationProgress` or
 * `readPersistedApplicationProgress`, which resolve the scope themselves.
 */
const applicationProgressLocalStorageKey = (
	offerSlug: string,
	accountId: string | null,
) =>
	accountId
		? `${APPLICATION_PROGRESS_KEY_PREFIX}${offerSlug}:Account:${accountId}`
		: `${APPLICATION_PROGRESS_KEY_PREFIX}${offerSlug}`;

/**
 * Whose records the app is currently reading and writing.
 *
 * - `undefined` — a session exists but its account id has not arrived yet.
 *   Reads return nothing and writes are buffered in memory; neither touches
 *   storage, because either choice of key would be wrong.
 * - `null` — signed out.
 * - a string — that account.
 *
 * Owned solely by `ApplicationProgressScope`. Read at call time (not captured in
 * a closure) so a write issued from a callback created before the session
 * changed still lands in the right place.
 */
export const applicationProgressScopeVar = makeVar<string | null | undefined>(
	undefined,
);

/**
 * Set when an authenticated session's account has not resolved within the
 * reporting deadline. The flow waits for the scope before redirecting, and
 * without this a failed account lookup would hold it on a loading placeholder
 * indefinitely; once stalled, callers proceed with what they can see rather
 * than waiting for something that is not coming.
 */
export const applicationProgressScopeStalledVar = makeVar(false);

/**
 * The session token the published scope was resolved for, set with the scope.
 * A token swapped in by another tab reaches a render before the owner's effect
 * can withdraw the scope, so a reader that must not show the previous account's
 * records for even one commit compares this with the session's current token
 * during render.
 */
export const applicationProgressScopeTokenVar = makeVar<string | null>(null);

// This module is reached from the root layout, so a storage failure here would
// take down every page rather than one feature. Private-mode SecurityError and
// QuotaExceededError both surface as throws from these calls.
const readStorage = (key: string): string | null => {
	try {
		return localStorage.getItem(key);
	} catch {
		return null;
	}
};

/**
 * Storage failures go to Sentry the way the portal's other non-hook reporters
 * do (`utils/unhandledValue.ts`): a static message so they group, the dynamic
 * part as a tag. The module cannot reach the logger context.
 *
 * The tag carries the offer and whether the key was account-scoped, never the
 * account id itself: the purpose is grouping, and an identifier is not needed
 * for that.
 */
const reportStorageFailure = (failure: "save" | "clear", key: string) => {
	Sentry.withScope((scope) => {
		scope.setLevel("warning");
		scope.setTag("storage_failure", failure);
		scope.setTag("storage_key", key.replace(/:Account:.*$/, ":Account"));
		Sentry.captureMessage("Application progress storage failure");
	});
};

/**
 * Reports whether the value was stored, so a caller can avoid dropping its
 * source.
 */
const writeStorage = (key: string, value: string): boolean => {
	try {
		localStorage.setItem(key, value);
		return true;
	} catch {
		reportStorageFailure("save", key);
		return false;
	}
};

const removeStorage = (key: string) => {
	try {
		localStorage.removeItem(key);
	} catch {
		reportStorageFailure("clear", key);
	}
};

export const initialApplicationProgress = (
	hasAuthenticatedSession: boolean,
): ApplicationProgressValue => ({
	investingEntityId: null,
	createdInvestingEntity: null,
	completedSteps: hasAuthenticatedSession ? ["account"] : [],
	activeStep: hasAuthenticatedSession ? "investing-entity" : "account",
	unitCountDecimal: null,
	investmentAmountHPM: null,
	investorPreference: null,
	reinvestmentPreference: null,
	hasDownloadedOfferDocument: null,
	downloadedOfferDocumentType: null,
	declaredInvestorType: null,
	hasCertifiedWholesale: null,
	isAdvised: null,
	adviserEmail: null,
	hasAdviserFees: null,
	initialAdviserFee: null,
	ongoingAdviserFee: null,
	customFieldSections: [],
});

/**
 * Clear an application while leaving the invest-page gate declaration standing.
 *
 * The declaration is not application data: it is the investor's answer to the
 * raise's gate, and it outlives any one application (`useOfferDeclaration`
 * re-hydrates it from localStorage so it persists across visits). It only lives
 * on this record so the invest page and the flow read one source of truth.
 *
 * Clearing it here would re-arm `useApplicationEntryGuard`, which reads these
 * same fields — and because the flow's sidebar is still mounted while the
 * post-submit `router.push("/orders")` is in flight, its `router.replace` back
 * to the invest page wins the race (APP-26309).
 *
 * `hasDownloadedOfferDocument` is deliberately reset. It is the legacy untyped
 * download flag, and on a legacy raise `InvestmentAmount` gates Continue on it;
 * preserving it would let a second application skip the forced in-flow
 * download, which would be a behaviour change with the flag off.
 */
export const resetApplicationProgress = (
	current: ApplicationProgressValue,
	hasAuthenticatedSession: boolean,
): ApplicationProgressValue => ({
	...initialApplicationProgress(hasAuthenticatedSession),
	declaredInvestorType: current.declaredInvestorType,
	hasCertifiedWholesale: current.hasCertifiedWholesale,
	downloadedOfferDocumentType: current.downloadedOfferDocumentType,
});

export const applicationProgressVar = makeVar<
	Record<string, ApplicationProgressValue>
>({});

/**
 * Read one offer's in-memory progress for display. `undefined` while the account
 * scope is unknown: the map is kept across that window so the read-modify-write
 * helpers keep a correct baseline, but it may belong to a different account than
 * the one now holding the session, so none of it is reported until the scope
 * says whose it is. Writers must read `applicationProgressVar` directly.
 */
export const readVisibleApplicationProgress = (
	offerSlug: string,
): ApplicationProgressValue | undefined => {
	if (
		applicationProgressScopeVar() === undefined &&
		!applicationProgressScopeStalledVar()
	) {
		return undefined;
	}
	return applicationProgressVar()[offerSlug];
};

/**
 * Read one offer's persisted progress for the current scope, defaulting fields
 * added in later versions. `null` when nothing is stored, the stored value can't
 * be parsed, or the scope is not yet known — never another scope's record.
 */
export const readPersistedApplicationProgress = (
	offerSlug: string,
	hasAuthenticatedSession: boolean,
): ApplicationProgressValue | null => {
	if (typeof window === "undefined") {
		return null;
	}
	const accountId = applicationProgressScopeVar();
	if (accountId === undefined) {
		return null;
	}
	const raw = readStorage(
		applicationProgressLocalStorageKey(offerSlug, accountId),
	);
	if (!raw) {
		return null;
	}
	try {
		const saved = JSON.parse(raw) as Partial<ApplicationProgressValue>;
		return { ...initialApplicationProgress(hasAuthenticatedSession), ...saved };
	} catch {
		return null;
	}
};

/**
 * Offers whose signed-out progress was written in this page session, and offers
 * whose record the map holds but storage does not — written while the account id
 * was unknown, or refused by storage. All are settled by
 * `setApplicationProgressScope`.
 */
const signedOutWrites = new Set<string>();

/**
 * Offers whose map record is an adoption merge — the account's saved record with
 * the gate's declaration over it — that storage could not take. A write built on
 * one of these already carries the declaration, so storing it retires the gate's
 * copy. A map rebuilt any other way (after a stall, from the account key the
 * failed write never reached) does not, which is why this is tracked rather than
 * inferred from the record's contents.
 */
const adoptionsInMap = new Set<string>();

/**
 * Signed-out offers whose latest record storage refused. The map holds the only
 * copy, and any value still under the signed-out key is an older one — so an
 * empty key cannot stand for "the write failed", and the stored value cannot be
 * trusted over the map.
 */
const unstoredSignedOutWrites = new Set<string>();
const bufferedWrites = new Set<string>();

/**
 * Whose records the in-memory map currently holds.
 *
 * Deliberately separate from the published scope. While the account id is
 * unknown the map keeps the entries it already had, because the flow's helpers
 * read-modify-write through it: emptying it would make `markStepComplete` and
 * friends rebuild `completedSteps` from initial values, so an investing-entity
 * switch — which mints a token and therefore opens that window — would wipe the
 * investor's real progress. The map is emptied instead at the moment the scope
 * resolves to a *different* account, and the writes made in between go with it.
 */
let mapScope: string | null | undefined;

/**
 * What the invest-page gate decides, and therefore what adoption carries across
 * a sign-in. `hasDownloadedOfferDocument` belongs here even though
 * `resetApplicationProgress` clears it: the two answer different questions.
 * Adoption records that this investor has just taken the document, which is the
 * defect this work fixes; the reset applies to a *new* application, where the
 * document must be taken again.
 */
const DECLARATION_FIELDS = [
	"declaredInvestorType",
	"hasCertifiedWholesale",
	"downloadedOfferDocumentType",
	"hasDownloadedOfferDocument",
] as const;

const withDeclarationFrom = (
	base: ApplicationProgressValue,
	source: ApplicationProgressValue,
): ApplicationProgressValue =>
	DECLARATION_FIELDS.reduce<ApplicationProgressValue>(
		(next, field) => ({ ...next, [field]: source[field] }),
		base,
	);

/**
 * The account this page session last resolved to, or `null` if none has. Set
 * whether or not adoption managed to store anything, so a record still pending
 * is known to be that account's retry.
 *
 * Adoption is offered to a session that has not signed anyone in yet, and to the
 * same investor signing back in — answering the gate, signing out and signing
 * back in is one person's own sequence. It is withheld from a *different*
 * account, because between a sign-out and the next sign-in the browser may have
 * changed hands, and a declaration decides which disclosure document is served.
 */
let lastSignedInAccountId: string | null = null;

/**
 * Consume the gate's signed-out copy, once a record built on the adoption merge
 * has reached the account key.
 *
 * That record carries the gate's answer — including an answer the investor has
 * since revised or cleared. Left pending, a later adoption would merge the old
 * one back over it and gate on the wrong document. Only a record known to be
 * built on the merge retires it: after a stall the map is rebuilt from an
 * account key the failed write never reached, and carries no declaration.
 */
const retireGateCopy = (offerSlug: string) => {
	if (adoptionsInMap.delete(offerSlug) && signedOutWrites.delete(offerSlug)) {
		unstoredSignedOutWrites.delete(offerSlug);
		removeStorage(applicationProgressLocalStorageKey(offerSlug, null));
	}
};

/** Persist one offer's progress under the current scope. */
export const persistApplicationProgress = (
	offerSlug: string,
	progress: ApplicationProgressValue,
) => {
	if (typeof window === "undefined") {
		return;
	}
	const accountId = applicationProgressScopeVar();
	if (accountId === undefined) {
		// Held until the account is known. The record itself lives in the map,
		// which is why the map must survive this window.
		bufferedWrites.add(offerSlug);
		return;
	}
	if (accountId === null) {
		signedOutWrites.add(offerSlug);
	}
	const stored = writeStorage(
		applicationProgressLocalStorageKey(offerSlug, accountId),
		JSON.stringify(progress),
	);
	if (accountId === null) {
		if (stored) {
			unstoredSignedOutWrites.delete(offerSlug);
		} else {
			unstoredSignedOutWrites.add(offerSlug);
		}
		return;
	}
	if (!stored) {
		// Storage refused it. The record is in the map, so hold it pending rather
		// than let the flow proceed as though it were saved — the next scope
		// resolution, which every investing-entity switch performs, retries it.
		bufferedWrites.add(offerSlug);
		return;
	}
	bufferedWrites.delete(offerSlug);
	retireGateCopy(offerSlug);
};

/**
 * The investing-entity create form's current internal sub-step
 * (`investing-entity` | `tax-details` | `nature-purpose`), or `null` when not
 * actively creating a new entity.
 *
 * In-memory only (deliberately NOT persisted to localStorage): the create form
 * steps through these sub-steps without changing the route, and the sidebar
 * reads this to highlight the active sub-step. Keeping it out of persisted
 * progress means a mid-creation refresh resumes on `investing-entity` rather
 * than a half-created sub-step.
 */
export const applicationCreateStepVar = makeVar<string | null>(null);

/**
 * The record the map holds but storage does not — a write storage refused, one
 * written before the account id arrived, or a signed-out declaration whose copy
 * into the account failed. `undefined` when storage is up to date.
 *
 * Hydration must return this rather than read storage over it: the stored record
 * is the older one, and reporting it as current would both show the investor a
 * step they have moved past and persist that older step when the pending write
 * is finally placed.
 */
export const readPendingApplicationProgress = (
	offerSlug: string,
): ApplicationProgressValue | undefined => {
	if (!bufferedWrites.has(offerSlug) && !signedOutWrites.has(offerSlug)) {
		return undefined;
	}
	return applicationProgressVar()[offerSlug];
};

/**
 * Give up on resolving the account, and release whatever is waiting on it.
 *
 * The map is dropped here even though the scope is still unknown. It is kept
 * across an unknown scope only as the baseline for a token that is about to
 * resolve; once we stop waiting, that justification is gone and the token may
 * belong to a different account, which must never be shown the previous one's
 * application. Writes buffered against that baseline go with it — their record
 * lived in the map — while signed-out records stay under their own key and can
 * still be adopted if an account does eventually resolve.
 */
const dropRetainedApplicationProgress = () => {
	// A signed-out record storage refused exists nowhere else. It was written
	// before any account was known, so it is not account-bound and dropping it
	// with the rest would lose the declaration outright rather than leaving it to
	// be retried. Records storage did take are already under their own key.
	//
	// Unless an account has since tried to adopt it: the map entry is then that
	// account's saved application with the answer merged in, and it goes with
	// everything else that account's. The token this drop serves may be someone
	// else's.
	const entries = applicationProgressVar();
	const kept: Record<string, ApplicationProgressValue> = {};
	signedOutWrites.forEach((offerSlug) => {
		const progress = entries[offerSlug];
		if (
			progress &&
			unstoredSignedOutWrites.has(offerSlug) &&
			!adoptionsInMap.has(offerSlug)
		) {
			kept[offerSlug] = progress;
		}
	});
	adoptionsInMap.clear();
	bufferedWrites.clear();
	mapScope = undefined;
	applicationProgressVar(kept);
};

export const releaseStalledApplicationProgressScope = (
	sessionToken: string | null,
) => {
	dropRetainedApplicationProgress();
	// The stall belongs to this token. Publishing it lets a reader that holds
	// on a token mismatch tell a release it may honour from a token swapped in
	// afterwards, which must still be held until the owner resolves it.
	applicationProgressScopeTokenVar(sessionToken);
	applicationProgressScopeStalledVar(true);
};

/**
 * Drop whatever the previous token left in an unbound map.
 *
 * Called when the session token changes. An unbound map is the token's own,
 * whichever account it turns out to belong to — writes made while its account
 * query was still in flight, or by a flow the stall released. A replacing token
 * may be a different account's, so none of it can carry over.
 *
 * A map bound to an account is left alone: it is that account's, and an
 * investing-entity switch mints a token without changing who is signed in.
 */
export const discardUnboundApplicationProgress = () => {
	if (mapScope !== undefined) {
		return;
	}
	dropRetainedApplicationProgress();
	applicationProgressScopeStalledVar(false);
};

/**
 * Consume the signed-out records still waiting to be adopted, when the session
 * token is replaced before any account has claimed them.
 *
 * A replacing token may be a different person's, and a record no account has
 * claimed has no owner to fall back to — so it is dropped rather than offered
 * to whoever signs in next. Once any account has resolved, what is still
 * pending is its own retry — adoption that storage refused — and stays.
 */
export const discardUnclaimedSignedOutProgress = () => {
	if (lastSignedInAccountId !== null) {
		return;
	}
	signedOutWrites.forEach((offerSlug) => {
		removeStorage(applicationProgressLocalStorageKey(offerSlug, null));
	});
	signedOutWrites.clear();
	unstoredSignedOutWrites.clear();
};

/**
 * Delete the records this scheme supersedes. Nothing reads them: a `V2` key is
 * byte-identical to a `V3` signed-out key, so they were versioned out rather
 * than migrated — a record written by a signed-in investor must not become the
 * next signed-out visitor's. Left in place they would sit in storage for the
 * life of the browser, against a quota this module already has to survive.
 *
 * Runs once per page session, on the first scope resolution.
 */
let hasSweptSupersededKeys = false;
const sweepSupersededApplicationProgress = () => {
	if (hasSweptSupersededKeys || typeof window === "undefined") {
		return;
	}
	hasSweptSupersededKeys = true;
	try {
		// Collected before removing any: deleting during the walk reindexes it.
		const superseded: string[] = [];
		for (let index = 0; index < localStorage.length; index += 1) {
			const key = localStorage.key(index);
			if (key?.startsWith(SUPERSEDED_KEY_PREFIX)) {
				superseded.push(key);
			}
		}
		superseded.forEach(removeStorage);
	} catch {
		// Storage is unavailable, so there is nothing to clean up.
	}
};

/**
 * Point the app at one account's records.
 *
 * Three transitions, and they are not symmetric:
 *
 * - to `undefined` — not a resolution. The account id has not arrived, so the
 *   map and the pending writes are left exactly as they are; only storage reads
 *   stop answering. The map is the baseline the flow reads and writes through,
 *   and every investing-entity switch passes through here.
 * - to an account — adopt this page session's signed-out progress, then either
 *   keep the map and place the buffered writes (the map already held this
 *   account) or empty both (it held someone else's, so anything written against
 *   it was built on the wrong baseline and cannot be trusted).
 * - to `null` — signed out; nothing pending survives.
 */
export const setApplicationProgressScope = (
	accountId: string | null | undefined,
	sessionToken: string | null = null,
) => {
	if (applicationProgressScopeVar() === accountId) {
		return;
	}

	sweepSupersededApplicationProgress();

	if (accountId === undefined) {
		applicationProgressScopeVar(accountId);
		return;
	}

	if (accountId === null) {
		// Two kinds of record are still pending here, and both go. One belongs to
		// the account that could not store it. The other was never claimed at all
		// — answered at the gate by someone whose account then failed to resolve.
		// Neither may outlive the session: after a sign-out the browser may have
		// changed hands, and leaving either under the signed-out key would put it
		// in front of whoever uses it next, untracked and unconsumable.
		signedOutWrites.forEach((offerSlug) => {
			removeStorage(applicationProgressLocalStorageKey(offerSlug, null));
		});
		signedOutWrites.clear();
		unstoredSignedOutWrites.clear();
		adoptionsInMap.clear();
		bufferedWrites.clear();
		mapScope = null;
		applicationProgressScopeStalledVar(false);
		applicationProgressScopeTokenVar(null);
		applicationProgressScopeVar(null);
		applicationProgressVar({});
		return;
	}

	// Merged records the account key could not take. The map still holds the
	// declaration-only signed-out record, which is a truncated baseline: hydration
	// would report it and the next write would persist it over the account's own
	// completed steps.
	const unstoredAdoptions = new Map<string, ApplicationProgressValue>();
	// Adoptions that DID store, for offers whose gate record the map was holding
	// as the only copy. Storage now has the merge; the map must report the same
	// thing or the flow reads a record with none of the account's own work in it.
	const storedAdoptions = new Map<string, ApplicationProgressValue>();
	// Read before the branches below reassign it.
	const isDifferentAccount = !(
		lastSignedInAccountId === null || lastSignedInAccountId === accountId
	);
	if (!isDifferentAccount) {
		let adopted = true;
		const inMemory = applicationProgressVar();
		signedOutWrites.forEach((offerSlug) => {
			const signedOutKey = applicationProgressLocalStorageKey(offerSlug, null);
			// Storage can refuse a write, in which case the map holds the gate's
			// latest answer and the key holds an older one or nothing. The map wins
			// there; otherwise the key is the source, with the map as a last resort.
			const refusedRecord = unstoredSignedOutWrites.has(offerSlug)
				? inMemory[offerSlug]
				: undefined;
			const signedOut = refusedRecord
				? JSON.stringify(refusedRecord)
				: (readStorage(signedOutKey) ??
					(inMemory[offerSlug] ? JSON.stringify(inMemory[offerSlug]) : null));
			if (!signedOut) {
				return;
			}
			const accountKey = applicationProgressLocalStorageKey(
				offerSlug,
				accountId,
			);
			const existing = readStorage(accountKey);
			let payload = signedOut;
			if (existing) {
				try {
					const fromGate = JSON.parse(signedOut) as ApplicationProgressValue;
					const saved = JSON.parse(existing) as Record<string, unknown>;
					// The account's application stands, but the declaration made at the
					// gate immediately before signing in is the investor's latest answer
					// to it. Keeping the older one would reopen the gate on a stale
					// choice and serve the document for the wrong investor type.
					DECLARATION_FIELDS.forEach((field) => {
						saved[field] = fromGate[field];
					});
					payload = JSON.stringify(saved);
				} catch {
					// The account's record is unreadable, so there is nothing to
					// merge into. The gate's answer is valid and stands on its own;
					// storing the malformed value would destroy it.
				}
			}
			// The signed-out record is only dropped once its contents are safely
			// under the account key, so a storage failure can be retried rather
			// than losing the declaration outright.
			if (writeStorage(accountKey, payload)) {
				removeStorage(signedOutKey);
				// A buffered write is the flow's own and the flush below owns the
				// map entry for it; only a map that exists solely because the write
				// was refused is superseded here.
				if (refusedRecord && !bufferedWrites.has(offerSlug)) {
					try {
						storedAdoptions.set(
							offerSlug,
							JSON.parse(payload) as ApplicationProgressValue,
						);
					} catch {
						// Unparseable, so the map's record is the best available.
					}
				}
				unstoredSignedOutWrites.delete(offerSlug);
			} else {
				adopted = false;
				try {
					unstoredAdoptions.set(
						offerSlug,
						JSON.parse(payload) as ApplicationProgressValue,
					);
				} catch {
					// Unparseable, so there is nothing better than the map's record.
				}
			}
		});
		if (adopted) {
			signedOutWrites.clear();
			unstoredSignedOutWrites.clear();
		}
		lastSignedInAccountId = accountId;
	} else {
		// A different account: these signed-out records are not its to take. They
		// are consumed rather than merely untracked — left in storage they would
		// resurface on the gate after a later sign-out, be re-tracked by the next
		// write, and then be adopted by this very account, which now matches
		// `lastSignedInAccountId`. A declaration withheld once stays withheld.
		signedOutWrites.forEach((offerSlug) => {
			removeStorage(applicationProgressLocalStorageKey(offerSlug, null));
		});
		signedOutWrites.clear();
		unstoredSignedOutWrites.clear();
		// It becomes the account this session last signed in, so its own later
		// sign-out-and-return can still adopt what it writes from here on.
		lastSignedInAccountId = accountId;
	}

	// `undefined` means the map has never been bound to an account: a fresh page
	// load whose gate was answered before the account query returned, or a stall
	// release. What it holds is then this session's own, and whichever account
	// resolves now is the one that wrote it.
	//
	// Unless a different account has signed in on this browser since — then it is
	// discarded however it came to be unbound. Withholding its records from
	// storage would mean nothing if they stayed on screen and were written back
	// under this account's key.
	if (
		!isDifferentAccount &&
		(mapScope === accountId || mapScope === undefined)
	) {
		// A buffered write built on this account's own progress is a complete
		// record and stands as it is.
		//
		// A write made while the map was unbound was not. `readPersistedApplication-
		// Progress` refuses to answer with the scope unknown, so the record behind
		// it is initial values plus whatever was done since — the gate's answer,
		// or a flow's work after a stall released it — and storing it whole over
		// a saved application would wipe the entity, the steps and the answers
		// that application holds. Over a saved record it contributes only the
		// declaration, and the map is rebased onto that merge: the record on
		// screen becomes the one every later write builds on, so it has to be
		// the saved one. Post-stall work is not carried past this point, which is
		// what the stall's toast said. With nothing saved there is nothing to
		// protect, and the record is stored whole.
		const isUnbound = mapScope === undefined;
		const entries = { ...applicationProgressVar() };
		const unplaced = new Set<string>();
		bufferedWrites.forEach((offerSlug) => {
			const progress = entries[offerSlug];
			if (!progress) {
				return;
			}
			const key = applicationProgressLocalStorageKey(offerSlug, accountId);
			let payload = progress;
			if (isUnbound) {
				const existing = readStorage(key);
				if (existing) {
					try {
						payload = withDeclarationFrom(
							JSON.parse(existing) as ApplicationProgressValue,
							progress,
						);
					} catch {
						// Unreadable, so the map's record is the best available.
					}
				}
			}
			entries[offerSlug] = payload;
			// Anything that fails to store stays pending rather than being
			// reverted on screen.
			if (writeStorage(key, JSON.stringify(payload))) {
				retireGateCopy(offerSlug);
			} else {
				unplaced.add(offerSlug);
			}
		});
		bufferedWrites.clear();
		unplaced.forEach((offerSlug) => bufferedWrites.add(offerSlug));
		applicationProgressVar(entries);
	} else {
		bufferedWrites.clear();
		adoptionsInMap.clear();
		applicationProgressVar({});
	}

	// Placed after the map decision above so the merged record survives it. This
	// is the investor's own — the declaration they made on their way in, over the
	// account's saved application — so it is the account's map now.
	if (storedAdoptions.size > 0) {
		const entries = { ...applicationProgressVar() };
		storedAdoptions.forEach((offerProgress, offerSlug) => {
			const current = entries[offerSlug];
			// The stored merge is the account's saved application under the gate's
			// answer. The map's record is that answer and nothing else, but it is
			// the newer one, so it supplies the declaration.
			entries[offerSlug] = current
				? withDeclarationFrom(offerProgress, current)
				: offerProgress;
		});
		applicationProgressVar(entries);
	}

	if (unstoredAdoptions.size > 0) {
		const entries = { ...applicationProgressVar() };
		unstoredAdoptions.forEach((offerProgress, offerSlug) => {
			// The flush above may have stored a write built on this merge and
			// retired the gate's copy — the account's record then carries the
			// investor's latest answer, and the merge rebuilt from the old one has
			// nothing left to supply.
			if (!signedOutWrites.has(offerSlug)) {
				return;
			}
			const current = entries[offerSlug];
			// A record survives here only when the map was kept, which means a
			// retry: the flow has been running against it, so it is newer than a
			// merge rebuilt from storage in every field the investor can change —
			// the declaration included, which the wizard can revise after the gate.
			// The merge only supplies a declaration the map never received.
			if (!current) {
				entries[offerSlug] = offerProgress;
			} else if (
				!adoptionsInMap.has(offerSlug) &&
				current.declaredInvestorType == null
			) {
				// A record the merge never reached — the map was rebuilt from an
				// account key the failed write never wrote to. A record already
				// carrying the merge is left alone even with no declaration: that
				// is the investor clearing it, which a retry must not undo.
				entries[offerSlug] = withDeclarationFrom(current, offerProgress);
			}
			adoptionsInMap.add(offerSlug);
		});
		applicationProgressVar(entries);
	}

	mapScope = accountId;
	applicationProgressScopeStalledVar(false);
	applicationProgressScopeTokenVar(sessionToken);
	applicationProgressScopeVar(accountId);
};
